import type { ReactNode } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'

import DualListGroup from '@/component/molecules/DualList/components/DualListGroup'
import type { DualListItem, RenderItemExtraParams } from '@/component/molecules/DualList/types'

type Props<T extends DualListItem> = {
  /** 그룹핑된 아이템 (Record<groupKey, items>) */
  groups: Record<string, T[]>
  /** 그룹 렌더링 순서 (지정 시 이 순서로 그룹을 렌더링) */
  orderedGroupKeys?: string[]
  /** 그룹이 열려있는지 확인하는 함수 */
  isGroupOpen: (key: string | number) => boolean
  /** 그룹 토글 함수 */
  onToggleGroup: (key: string | number) => void
  /** 아이템 이동 함수 */
  onMoveItems: (items: T[]) => void
  /** 그룹 키를 라벨로 변환하는 함수 */
  getGroupLabel: (key: string | number) => string
  /** 아이템 비활성화 여부 판단 함수 */
  disabledFilter?: (item: T) => boolean
  /** 아이템 추가 렌더링 함수 */
  renderItemExtra?: (params: RenderItemExtraParams<T>) => ReactNode
  /** 아이템 커스텀 렌더링 함수 (제공 시 DualListItem 대신 사용) */
  renderItem?: (item: T, onClick: () => void) => ReactNode
  /** Empty 뷰 (null이면 리스트 렌더링) */
  emptyView: ReactNode | null
}

/**
 * 그룹핑된 오른쪽 컨텐츠 컴포넌트
 * - Empty 상태 처리
 * - 그룹별 DualListGroup 렌더링
 */
export const GroupedRightContent = <T extends DualListItem>({
  groups,
  orderedGroupKeys,
  isGroupOpen,
  onToggleGroup,
  onMoveItems,
  getGroupLabel,
  disabledFilter,
  renderItemExtra,
  renderItem,
  emptyView,
}: Props<T>) => {
  if (emptyView) return <>{emptyView}</>

  const groupKeys = orderedGroupKeys ?? Object.keys(groups)

  return (
    <Scrollbars>
      {groupKeys.map((groupKey) => {
        const groupItems = groups[groupKey] ?? []
        if (groupItems.length === 0) return null

        const enabledItems = disabledFilter
          ? groupItems.filter((item) => !disabledFilter(item))
          : groupItems

        return (
          <DualListGroup
            key={groupKey}
            groupKey={groupKey}
            groupLabel={getGroupLabel(groupKey)}
            items={groupItems}
            side="right"
            isOpen={isGroupOpen(groupKey)}
            onToggle={() => onToggleGroup(groupKey)}
            onMoveAll={() => onMoveItems(enabledItems)}
            disabledFilter={disabledFilter}
            renderItemExtra={renderItemExtra}
            renderItem={renderItem}
            onItemClick={(item) => onMoveItems([item])}
          />
        )
      })}
    </Scrollbars>
  )
}

export default GroupedRightContent
